import { VERSION_NODE } from '@/config/versionConfig'
import { spawnSync } from 'child_process'

const NPM_META_URL = 'https://registry.npmjs.org/@gabrielhicks/solv/latest'

const run = (cmd: string): number => {
  const r = spawnSync(cmd, { shell: true, stdio: 'inherit' })
  if (r.status !== 0) {
    console.error(`[updateSolv] '${cmd}' exited with status ${r.status ?? 'null'}`)
  }
  return r.status ?? 1
}

type LatestMeta = {
  version: string | null // null means "fall back to @latest"
  targetNode: string
}

// Pull the LATEST published version + its declared targetNode straight from the
// npm registry. We use this so `solv update`:
//   1. Pins the install to an explicit version (no @latest cache ambiguity), and
//   2. Installs the Node version the published package wants in the same cycle.
const fetchLatestMeta = async (): Promise<LatestMeta> => {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 5000)
    const res = await fetch(NPM_META_URL, { signal: controller.signal })
    clearTimeout(timer)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = (await res.json()) as {
      version?: string
      solv?: { targetNode?: string }
    }
    return {
      version: data.version ?? null,
      targetNode: data?.solv?.targetNode ?? VERSION_NODE,
    }
  } catch (err) {
    console.warn(
      `[updateSolv] could not fetch latest meta from npm, falling back to defaults: ${(err as Error).message}`,
    )
    return { version: null, targetNode: VERSION_NODE }
  }
}

export const updateSolv = async (): Promise<void> => {
  const meta = await fetchLatestMeta()

  // 1. Upgrade Node FIRST, using the currently-installed pnpm.
  //    Order matters: `pnpm self-update` can move pnpm to a version that
  //    requires a newer Node than the box has. We've seen pnpm 11.5.1 require
  //    Node 22.13 (it imports `node:sqlite`) — self-updating before Node leaves
  //    pnpm crashing with ERR_UNKNOWN_BUILTIN_MODULE. Installing Node first
  //    ensures the newly-self-updated pnpm always has a compatible runtime.
  //
  //    Note: we deliberately use the (deprecated) `pnpm env use … --global`
  //    instead of `pnpm runtime set node … -g`. The latter has a bug where it
  //    still requires a project manifest even with `-g`, erroring with
  //    ERR_PNPM_NO_IMPORTER_MANIFEST_FOUND when run from $HOME. `env use` is
  //    deprecated but functional and is the only form that works for a truly
  //    global runtime install without a package.json.
  run(`pnpm env use ${meta.targetNode} --global`)

  // 2. Now safe to self-update pnpm — Node is current.
  run('pnpm self-update')

  // 3. Install the latest solv. Pin to an explicit version when we know it —
  //    pnpm's `@latest` resolution is cached and will silently keep an
  //    already-installed version (we saw `+ ... 5.8.21 (5.8.22 is available)`
  //    with `added 0`). Pinning bypasses that cache entirely.
  //
  //    `--config.minimumReleaseAge=0` disables pnpm 11.5+'s default 24-hour
  //    quarantine for newly-published packages (ERR_PNPM_MINIMUM_RELEASE_AGE_VIOLATION).
  //    The policy is a supply-chain safety net against typosquats, but for a
  //    package we publish ourselves it just blocks our own users from getting
  //    fixes for a day after every release.
  const versionSpec = meta.version ? `@${meta.version}` : '@latest'
  run(`pnpm add -g --config.minimumReleaseAge=0 @gabrielhicks/solv${versionSpec}`)
}
